import { useState } from 'react';
import { useAsyncEffect } from '../../hooks';
import Stylies from './StyliesManager';
import type { PublicProvider } from '../../api/Providers';
import type { ClientProviderProfile } from '../../api/ProfileClientView';
import FeatureFlags from '../FeatureFlags';
import { STYLIE_AWARDS_FLAG_NAME } from './constants';
import type { UseGetProStylieAwardReturnType, UseIsStylieAwardeeReturnType } from './types';
import nonCriticalException from '../exceptionLogger';

export const isStylieAwardee = async (
  profile: PublicProvider | ClientProviderProfile,
): Promise<boolean> => {
  const isEnabled = await FeatureFlags.isEnabled(STYLIE_AWARDS_FLAG_NAME);
  if (!isEnabled) {
    return false;
  }
  return (await Stylies.getProviderAward(profile)).is_awardee;
};

export const useIsStylieAwardee = (
  profile: PublicProvider | ClientProviderProfile,
): UseIsStylieAwardeeReturnType => {
  const [returnData, setReturnData] = useState<UseIsStylieAwardeeReturnType>({
    is_awardee: false,
    isLoading: true,
  });

  useAsyncEffect(async () => {
    setReturnData({
      ...returnData,
      isLoading: true,
    });
    try {
      const newIsAwardee = await isStylieAwardee(profile);
      setReturnData({
        is_awardee: newIsAwardee,
        isLoading: false,
      });
    } catch (e) {
      nonCriticalException(e);
      setReturnData({
        ...returnData,
        isLoading: false,
      });
    }
  }, []);

  return returnData;
};

export const useGetProStylieAward = (
  profile: PublicProvider | ClientProviderProfile,
): UseGetProStylieAwardReturnType => {
  const [returnData, setReturnData] = useState<UseGetProStylieAwardReturnType>({
    awardYear: '',
    awardData: { is_awardee: false },
    isLoading: true,
  });

  useAsyncEffect(async () => {
    setReturnData({
      ...returnData,
      isLoading: true,
    });
    try {
      const newAwardData = await Stylies.getProviderAward(profile);
      const awardYear = await Stylies.getAwardYear();
      setReturnData({
        awardYear,
        awardData: newAwardData,
        isLoading: false,
      });
    } catch (e) {
      nonCriticalException(e);
      setReturnData({
        ...returnData,
        isLoading: false,
      });
    }
  }, []);

  return returnData;
};
